/**
 * Clientes fundadores — `OD-0059` D-06.
 *
 * Lo único que se escribe aquí es «la llamada N se hizo». La inscripción la
 * hace el cupón en Stripe y el cobro lo decide el plazo, así que la pantalla
 * no tiene ni un botón de alta, ni de baja, ni de perdonar un cobro.
 *
 * Marcar es lo que evita el cobro y no se deshace, por eso pide confirmación:
 * un clic por error regalaría el año a quien no ha participado.
 */
import { useCallback, useEffect, useState } from "react";
import { api, type Fundador, type ListaFundadores } from "../api/cliente";
import { t } from "../textos";

function fecha(iso: string | null): string {
  if (iso === null) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function Fundadores() {
  const [estado, setEstado] = useState<"cargando" | "ok" | "no-encontrado" | "sin-mfa" | "error">("cargando");
  const [lista, setLista] = useState<ListaFundadores | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<{ readonly tono: "exito" | "error"; readonly texto: string } | null>(null);

  const cargar = useCallback(async (): Promise<void> => {
    const r = await api.fundadores();
    if (r.ok) {
      setLista(r.datos);
      setEstado("ok");
      return;
    }
    if (r.estado === 404 || r.estado === 401) setEstado("no-encontrado");
    else if (r.estado === 403) setEstado("sin-mfa");
    else setEstado("error");
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function marcar(f: Fundador, n: 1 | 2): Promise<void> {
    const nombre = f.nombre ?? f.organizationId;
    if (!window.confirm(t.fundadores.confirmar(n, nombre))) return;
    setMensaje(null);
    setOcupado(f.organizationId);
    const r = await api.marcarLlamada(f.organizationId, n);
    setOcupado(null);
    setMensaje(r.ok ? { tono: "exito", texto: t.fundadores.marcada(n, nombre) } : { tono: "error", texto: t.comun.error });
    await cargar();
  }

  if (estado === "cargando") return <p className="suave">{t.comun.cargando}</p>;
  if (estado === "no-encontrado") return <p>{t.comun.noEncontrado}</p>;
  if (estado === "sin-mfa") return <p className="aviso" data-tono="aviso">{t.comun.sinMfa}</p>;
  if (estado === "error" || lista === null) return <p className="aviso" data-tono="error">{t.comun.error}</p>;

  const celda = (f: Fundador, n: 1 | 2) => {
    const l = n === 1 ? f.llamada1 : f.llamada2;
    if (l.hecha !== null) return <span>{t.fundadores.hecha(fecha(l.hecha), l.por)}</span>;
    const abierto = f.estado === "EN_CURSO";
    return (
      <>
        <span className="suave">{t.fundadores.antesDe(fecha(l.limite))}</span>
        {abierto ? (
          <div>
            <button type="button" data-variante="secundario" onClick={() => void marcar(f, n)} disabled={ocupado !== null}>
              {t.fundadores.marcar}
            </button>
          </div>
        ) : null}
      </>
    );
  };

  return (
    <>
      <h1>{t.fundadores.titulo}</h1>
      <p>
        <strong>{t.fundadores.plazas(lista.libres, lista.plazas)}</strong>
      </p>
      <p className="aviso" data-tono="aviso">{t.fundadores.explicacion}</p>
      <p className="suave">{t.fundadores.inscripcion}</p>
      {mensaje !== null ? (
        <p className="aviso" data-tono={mensaje.tono} role="status">
          {mensaje.texto}
        </p>
      ) : null}

      {lista.fundadores.length === 0 ? (
        <p className="suave">{t.fundadores.vacio}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t.fundadores.columnas.nombre}</th>
              <th>{t.fundadores.columnas.dia}</th>
              <th>{t.fundadores.columnas.llamada1}</th>
              <th>{t.fundadores.columnas.llamada2}</th>
              <th>{t.fundadores.columnas.estado}</th>
            </tr>
          </thead>
          <tbody>
            {lista.fundadores.map((f) => (
              <tr key={f.organizationId}>
                <td>
                  {f.nombre ?? t.fundadores.sinNombre}
                  <div className="suave">{f.organizationId}</div>
                </td>
                <td>{t.fundadores.dia(f.dia)}</td>
                <td>{celda(f, 1)}</td>
                <td>{celda(f, 2)}</td>
                <td>
                  {t.fundadores.estados[f.estado] ?? f.estado}
                  {f.estado === "EN_CURSO" && f.fechaDeCobro !== null ? (
                    <div className="suave">{t.fundadores.avisoEnviado(fecha(f.fechaDeCobro))}</div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
